import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  ThemeProvider,
  createTheme,
  CssBaseline,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import backgroundImage from '../assets/background-image-3.avif';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#ff4081' },
    secondary: { main: '#00e5ff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif' },
});

const MotionBox = motion(Box);

function HowItWorks() {
  const { ref: heroRef, inView: heroInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Back Button */}
        <Box sx={{ position: 'fixed', top: 20, left: 20, zIndex: 1000 }}>
          <Button
            component={Link}
            to="/"
            size="small"
            variant="contained"
            sx={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: 'text.primary',
              '&:hover': { background: 'rgba(255, 255, 255, 0.2)' },
            }}
          >
            ← Back
          </Button>
        </Box>

        {/* Hero Section */}
        <Box
          ref={heroRef}
          sx={{
            minHeight: '40vh',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background Image with Parallax */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(45deg, rgba(18, 18, 18, 0.9) 0%, rgba(18, 18, 18, 0.7) 100%)',
              },
            }}
          />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h1"
                align="center"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #ff4081 30%, #00e5ff 90%)',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  mb: 2,
                  fontSize: { xs: '2.5rem', md: '4rem' },
                }}
              >
                How It Works
              </Typography>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: 'text.secondary',
                  maxWidth: '800px',
                  mx: 'auto',
                  mb: 4,
                }}
              >
                Get started with ArtistPlay in just 3 simple steps
              </Typography>
            </MotionBox>
          </Container>
        </Box>

        {/* Steps Section */}
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: { xs: 4, lg: 6 },
              alignItems: 'center',
            }}
          >
            {[
              {
                step: '1',
                title: 'Setup Account',
                description: 'Create your DJ profile and connect your Stripe account to enable tip payments. This one-time setup takes just a few minutes to get you ready.',
                icon: '🎧',
                gradient: 'linear-gradient(135deg, rgba(255, 64, 129, 0.1) 0%, rgba(0, 229, 255, 0.1) 100%)',
              },
              {
                step: '2',
                title: 'Share QR Code',
                description: 'Display your unique QR code at your event for guests to scan. They can request songs and send tips without downloading any app.',
                icon: '📱',
                gradient: 'linear-gradient(135deg, rgba(0, 229, 255, 0.1) 0%, rgba(255, 64, 129, 0.1) 100%)',
              },
              {
                step: '3',
                title: 'Get Paid',
                description: 'Receive song requests and tips directly to your Stripe account in real-time. Keep the crowd engaged while earning money for your performance.',
                icon: '💰',
                gradient: 'linear-gradient(135deg, rgba(255, 64, 129, 0.1) 0%, rgba(0, 229, 255, 0.1) 100%)',
              },
            ].map((item, index) => (
              <MotionBox
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                sx={{ flex: 1, width: '100%' }}
              >
                                 <Paper
                   sx={{
                     p: 3,
                     minHeight: '320px',
                     background: item.gradient,
                     border: '1px solid rgba(255, 255, 255, 0.1)',
                     borderRadius: 3,
                     backdropFilter: 'blur(20px)',
                     transition: 'transform 0.3s ease-in-out',
                     display: 'flex',
                     flexDirection: 'column',
                     alignItems: 'center',
                     textAlign: 'center',
                     '&:hover': {
                       transform: 'translateY(-4px)',
                       boxShadow: '0 12px 24px rgba(0, 0, 0, 0.2)',
                     },
                   }}
                 >
                   <Box
                     sx={{
                       width: 50,
                       height: 50,
                       borderRadius: '50%',
                       background: 'linear-gradient(45deg, #ff4081 30%, #00e5ff 90%)',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       mb: 2,
                       fontSize: '1.25rem',
                       fontWeight: 700,
                       color: 'white',
                       flexShrink: 0,
                     }}
                   >
                     {item.step}
                   </Box>
                   <Typography
                     sx={{
                       fontSize: '2.5rem',
                       mb: 2,
                       flexShrink: 0,
                     }}
                   >
                     {item.icon}
                   </Typography>
                   <Typography
                     variant="h5"
                     sx={{
                       fontWeight: 600,
                       color: 'text.primary',
                       mb: 2,
                       fontSize: { xs: '1.25rem', sm: '1.5rem' },
                       flexShrink: 0,
                     }}
                   >
                     {item.title}
                   </Typography>
                   <Typography
                     variant="body1"
                     sx={{
                       color: 'text.secondary',
                       lineHeight: 1.6,
                       fontSize: { xs: '0.875rem', sm: '0.95rem' },
                       flex: 1,
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                     }}
                   >
                     {item.description}
                   </Typography>
                 </Paper>
              </MotionBox>
            ))}
          </Box>
        </Container>

        {/* CTA Section */}
        <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
          <Container maxWidth="md">
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              sx={{ textAlign: 'center' }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #ff4081 30%, #00e5ff 90%)',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  mb: 2,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                }}
              >
                Ready to Get Started?
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  mb: 4,
                  maxWidth: '600px',
                  mx: 'auto',
                }}
              >
                We've launched our beta version. Sign up now to get early access.
              </Typography>
              <Button
                component="a"
                href="https://app.artistplay.io"
                rel="noopener noreferrer"
                variant="contained"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  background: 'linear-gradient(45deg, #ff4081 30%, #00e5ff 90%)',
                  color: 'white',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'linear-gradient(45deg, #ff4081 40%, #00e5ff 100%)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                Sign up now — it’s free
              </Button>
            </MotionBox>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default HowItWorks;
